import {useMemo} from 'react'

import {usePinGlobalMeasurementNameMutation} from '../../../hooks/mutations/usePinGlobalMeasurementNameMutation'
import {usePinnedGlobalMeasurementNameQuery} from '../../../hooks/queries/usePinnedGlobalMeasurementNameQuery'
import {ParsedPinnedValue, PinnedIdItem} from '../../../types/DistributedCache'
const KEY_IDENTIFIER_PREFIX = 'settings-measurements'

interface UseManagePinningRowsProps {
  plantCode?: string
  selectedCategoryId: string
}

function normalizePinnedIds(values: PinnedIdItem[]): number[] {
  return values
    .map((item) => {
      if (typeof item === 'number') return item
      if (typeof item === 'string') {
        const converted = Number(item)
        return Number.isFinite(converted) ? converted : undefined
      }
      if (typeof item === 'object' && item !== null) {
        const candidate = item.globalMeasurementTypeId
        if (typeof candidate === 'number') return candidate
        const idCandidate = item.id
        if (typeof idCandidate === 'number') return idCandidate
      }
      return undefined
    })
    .filter((id): id is number => id !== undefined)
}

function parsePinnedIdsForCategory(value: string | undefined, categoryId: string): number[] {
  if (!value) {
    return []
  }

  try {
    const parsed = JSON.parse(value) as ParsedPinnedValue

    if (Array.isArray(parsed)) {
      return normalizePinnedIds(parsed)
    }

    if (typeof parsed !== 'object' || parsed === null) {
      return []
    }

    const categoryValues = parsed[categoryId]
    if (Array.isArray(categoryValues)) {
      return normalizePinnedIds(categoryValues)
    }

    const legacyValues = parsed.legacy
    if (Array.isArray(legacyValues)) {
      return normalizePinnedIds(legacyValues)
    }

    return []
  } catch {
    return []
  }
}

export const useManagePinningRows = ({
  plantCode,
  selectedCategoryId
}: UseManagePinningRowsProps) => {
  const keyIdentifier = plantCode
    ? `${KEY_IDENTIFIER_PREFIX}-${plantCode}-${selectedCategoryId}`
    : ''
  const {data: pinnedData} = usePinnedGlobalMeasurementNameQuery(keyIdentifier)
  const {mutate: pinGlobalMeasurementName, isPending: isPinGlobalMeasurementNamePending} =
    usePinGlobalMeasurementNameMutation()

  const pinnedValue = typeof pinnedData?.value === 'string' ? pinnedData.value : undefined

  const selectedCategoryPinnedIds = useMemo(
    () => new Set(parsePinnedIdsForCategory(pinnedValue, selectedCategoryId)),
    [pinnedValue, selectedCategoryId]
  )

  const handlePinGlobalMeasurementType = (globalMeasurementTypeId: number) => {
    if (!plantCode) {
      return
    }

    const currentPinnedIds = Array.from(selectedCategoryPinnedIds)
    const isAlreadyPinned = selectedCategoryPinnedIds.has(globalMeasurementTypeId)
    const nextPinnedIds = isAlreadyPinned
      ? currentPinnedIds.filter((id) => id !== globalMeasurementTypeId)
      : [...new Set([...currentPinnedIds, globalMeasurementTypeId])]

    pinGlobalMeasurementName({
      keyIdentifier,
      value: JSON.stringify(nextPinnedIds)
    })
  }

  return {
    selectedCategoryPinnedIds,
    handlePinGlobalMeasurementType,
    isPinGlobalMeasurementNamePending
  }
}
